/** Webenv-specific chain control tools (7 tools). */

import { parseEther } from "viem";

import {
  fastForward,
  impersonate,
  mineBlocks,
  setEthBalance,
  setTimestamp,
  setTokenBalance,
  stopImpersonating,
} from "../../runtime/chain-controls.js";

import type { TevmClients } from "../context.js";
import type { ToolResult } from "../types.js";
import type { Address, Hex } from "viem";

function toToolResult(data: unknown): ToolResult {
  return { content: [{ type: "text", text: JSON.stringify(data, null, 2) }] };
}

function toErrorResult(message: string): ToolResult {
  return {
    content: [{ type: "text", text: JSON.stringify({ error: message }) }],
    isError: true,
  };
}

export async function mineBlocksHandler(
  params: Record<string, unknown>,
  clients: TevmClients,
): Promise<ToolResult> {
  const count = Number(params.count ?? 1);
  const interval = params.interval ? Number(params.interval) : undefined;
  try {
    const result = await mineBlocks(clients, count, interval);
    return toToolResult({ mined: count, ...result });
  } catch (err) {
    return toErrorResult(
      `Mining failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function fastForwardHandler(
  params: Record<string, unknown>,
  clients: TevmClients,
): Promise<ToolResult> {
  const seconds = Number(params.seconds);
  if (!seconds || seconds <= 0) {
    return toErrorResult("seconds must be a positive number");
  }
  try {
    const result = await fastForward(clients, seconds);
    return toToolResult(result);
  } catch (err) {
    return toErrorResult(
      `Fast forward failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function setTimestampHandler(
  params: Record<string, unknown>,
  clients: TevmClients,
): Promise<ToolResult> {
  const timestamp = Number(params.timestamp);
  if (!timestamp) {
    return toErrorResult("timestamp is required (unix seconds)");
  }
  try {
    await setTimestamp(clients, BigInt(timestamp));
    return toToolResult({ timestamp, message: "Timestamp set" });
  } catch (err) {
    return toErrorResult(
      `Set timestamp failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function setBalanceHandler(
  params: Record<string, unknown>,
  clients: TevmClients,
): Promise<ToolResult> {
  const address = params.address as Address;
  const value = params.value as string;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!address || !value) {
    return toErrorResult("address and value (wei string) are required");
  }
  try {
    await setEthBalance(clients, address, BigInt(value));
    return toToolResult({ address, balance: value, message: "Balance set" });
  } catch (err) {
    return toErrorResult(
      `Set balance failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function mintTokensHandler(
  params: Record<string, unknown>,
  clients: TevmClients,
): Promise<ToolResult> {
  const token = params.token as Address;
  const to = params.to as Address;
  const amount = params.amount as string;
  const slot = params.slot !== undefined ? Number(params.slot) : 0;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!token || !to || !amount) {
    return toErrorResult("token, to, and amount are required");
  }
  try {
    await setTokenBalance(clients, token, to, slot, BigInt(amount));
    return toToolResult({ token, to, amount, message: "Tokens minted" });
  } catch (err) {
    return toErrorResult(
      `Mint failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function fundAccountHandler(
  params: Record<string, unknown>,
  clients: TevmClients,
): Promise<ToolResult> {
  const address = params.address as Address;
  const ethAmount = params.ethAmount ? (params.ethAmount as string) : "1000";
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!address) {
    return toErrorResult("address is required");
  }
  try {
    await setEthBalance(clients, address, parseEther(ethAmount));
    return toToolResult({ address, ethAmount, message: "Account funded" });
  } catch (err) {
    return toErrorResult(
      `Fund failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function impersonateHandler(
  params: Record<string, unknown>,
  clients: TevmClients,
): Promise<ToolResult> {
  const from = params.from as Address;
  const to = params.to as Address;
  const data = params.data as Hex | undefined;
  const value = params.value ? BigInt(params.value as string) : undefined;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!from || !to) {
    return toErrorResult("from and to addresses are required");
  }
  try {
    await impersonate(clients, from);
    const hash = await clients.walletClient.sendTransaction({
      account: from,
      to,
      data,
      value,
      chain: null,
    });
    await stopImpersonating(clients, from);
    return toToolResult({
      hash,
      from,
      to,
      message: "Impersonated transaction sent",
    });
  } catch (err) {
    try {
      await stopImpersonating(clients, from);
    } catch {
      /* best-effort cleanup */
    }
    return toErrorResult(
      `Impersonate failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** All chain control tool definitions for registration. */
export const CHAIN_CONTROL_TOOLS = [
  {
    name: "mine_blocks",
    description: "Mine N blocks with optional time interval between them",
    handler: mineBlocksHandler,
  },
  {
    name: "fast_forward",
    description: "Advance chain time by N seconds and mine a block",
    handler: fastForwardHandler,
  },
  {
    name: "set_timestamp",
    description: "Set the chain timestamp to an absolute value",
    handler: setTimestampHandler,
  },
  {
    name: "set_balance",
    description: "Set an address's ETH balance (value in wei)",
    handler: setBalanceHandler,
  },
  {
    name: "mint_tokens",
    description: "Mint ERC-20 tokens to an address by manipulating storage",
    handler: mintTokensHandler,
  },
  {
    name: "fund_account",
    description: "Fund an address with ETH (default 1000 ETH)",
    handler: fundAccountHandler,
  },
  {
    name: "impersonate",
    description: "Send a transaction from any address (impersonation)",
    handler: impersonateHandler,
  },
] as const;
